import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePasswordValidation, isPasswordValid } from '../hooks/usePasswordValidation';
import { mockFirms } from '../api/mockData';
import client from '../api/client';

interface Firm {
  id: string;
  name: string;
}

const PWD_REQS = [
  { id: 'len', label: '>8 chars', test: (p: string) => p.length >= 8 },
  { id: 'special', label: 'One special char', test: (p: string) => /[!@#$%^&*()\-_=+\[\]{};:'",.<>?/\\|`~]/.test(p) },
  { id: 'upper', label: 'Uppercase (A–Z)', test: (p: string) => /[A-Z]/.test(p) },
  { id: 'lower', label: 'Lowercase (a–z)', test: (p: string) => /[a-z]/.test(p) },
  { id: 'digit', label: 'Number (0–9)', test: (p: string) => /[0-9]/.test(p) },
];

const PasswordChecker: React.FC<{ password: string }> = ({ password }) => {
  if (!password) return null;

  const item = (r: typeof PWD_REQS[0], labelOverride?: string) => {
    const met = r.test(password);
    return (
      <div key={r.id} className={`pwd-req-item ${met ? 'met' : 'unmet'}`} data-req-id={r.id}>
        <span className="pwd-req-icon">{met ? '✓' : '·'}</span>
        <span>{labelOverride ?? r.label}</span>
      </div>
    );
  };

  const byId = (id: string) => PWD_REQS.find(r => r.id === id)!;

  return (
    <div className="pwd-req">
      <div className="pwd-req-title">Password requirements</div>

      {/* Desktop: 2 rows (2-col then 3-col) */}
      <div className="pwd-req-desktop">
        <div className="pwd-req-row pwd-req-row-2">
          {item(byId('len'), 'Greater than 8 chars')}
          {item(byId('special'), 'One special (!@#$%^&*-_=+...)')}
        </div>
        <div className="pwd-req-row pwd-req-row-3">
          {item(byId('upper'))}
          {item(byId('lower'))}
          {item(byId('digit'))}
        </div>
      </div>

      {/* Mobile: 2 rows (2-col, 3-col) */}
      <div className="pwd-req-mobile">
        <div className="pwd-req-row pwd-req-row-2">
          {item(byId('len'), '>8 characters')}
          {item(byId('special'), 'One special char')}
        </div>
        <div className="pwd-req-row pwd-req-row-3">
          {item(byId('upper'))}
          {item(byId('lower'))}
          {item(byId('digit'))}
        </div>
      </div>
    </div>
  );
};

export const RegisterPage: React.FC = () => {
  const [step, setStep] = useState<'form' | 'verify' | 'pending'>('form');
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    confirm: '',
    firm: '',
  });
  const [firms, setFirms] = useState<Firm[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const passwordChecks = usePasswordValidation(form.password);
  const allMet = isPasswordValid(passwordChecks);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchFirms = async () => {
      try {
        const response = await client.get('/firms');
        setFirms(response.data.firms);
      } catch (err) {
        console.warn('Using mock firms');
        setFirms(mockFirms);
      }
    };
    fetchFirms();
  }, []);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Full name is required.';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'A valid email address is required.';
    if (!allMet) e.password = 'Password does not meet all requirements.';
    if (form.password !== form.confirm) e.confirm = 'Passwords do not match.';
    if (!form.firm) e.firm = 'Please select your firm.';
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setIsLoading(true);
    try {
      await client.post('/auth/register', {
        name: form.name.trim(),
        email: form.email,
        password: form.password,
        confirmPassword: form.confirm,
        firmId: form.firm,
      });

      setStep('verify');
    } catch (err: any) {
      setErrors({
        submit: err.response?.data?.error || 'Registration failed. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const field = (id: keyof typeof form) => ({
    value: form[id],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setForm(f => ({ ...f, [id]: e.target.value }));
      setErrors(er => ({ ...er, [id]: '' }));
    },
  });

  if (step === 'verify') {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 16, color: 'var(--c-primary)', border: '2px solid var(--c-primary)', borderRadius: 8, width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>@</div>
          <h2 className="auth-title">Verify Your Email</h2>
          <p className="auth-subtitle">
            We've sent a verification link to <strong>{form.email}</strong>.
          </p>
          <p className="auth-subtitle">
            Check your inbox (and spam folder) and click the link to confirm your email address.
          </p>
          <button className="btn btn-primary btn-full" onClick={() => navigate('/verify-email')}>
            ✓ I've Verified My Email
          </button>
        </div>
      </div>
    );
  }

  if (step === 'pending') {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16, color: 'var(--c-primary)' }}>◌</div>
          <h2 className="auth-title">Awaiting Approval</h2>
          <p className="auth-subtitle">Your email has been verified. Your account is now pending approval from the RDF administrator.</p>
          <div className="alert alert-info" style={{ textAlign: 'left' }}>
            The RDF administrator will review your application and you will be notified by email once your account is approved.
          </div>
          <button className="btn btn-outline btn-full" onClick={() => navigate('/login')}>
            Back to Log In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page" style={{ alignItems: 'flex-start', paddingTop: 'calc(var(--nav-h) + 32px)' }}>
      <div className="auth-card" style={{ maxWidth: 520, margin: '0 auto' }}>
        <div className="auth-logo">
          <img src="/RDF_Logo.svg" alt="RDF" className="auth-logo-img" />
          <span className="auth-logo-text">Retail Derivatives Forum</span>
        </div>
        <h2 className="auth-title">Create Account</h2>
        <p className="auth-subtitle">
          Register to access documents, best practice guidance, and regulatory discussions. Access will only be granted to members.
        </p>

        {errors.submit && <div className="alert alert-error">{errors.submit}</div>}

        <form onSubmit={handleSubmit} noValidate>
          <div className="input-row">
            <div className="form-group">
              <label className="form-label">Full Name <span className="req">*</span></label>
              <input
                className={`form-input ${errors.name ? 'is-error' : ''}`}
                {...field('name')}
                placeholder="Jane Smith"
                autoComplete="name"
              />
              {errors.name && <div className="form-error">{errors.name}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Email Address <span className="req">*</span></label>
              <input
                className={`form-input ${errors.email ? 'is-error' : ''}`}
                type="email"
                {...field('email')}
                placeholder="jane@firm.com"
                autoComplete="email"
              />
              {errors.email && <div className="form-error">{errors.email}</div>}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Member Firm <span className="req">*</span></label>
            <select
              className={`form-input ${errors.firm ? 'is-error' : ''}`}
              {...field('firm')}
            >
              <option value="">Select your firm…</option>
              {[...firms].sort((a, b) => a.name.localeCompare(b.name)).map(f => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
            {errors.firm && <div className="form-error">{errors.firm}</div>}
            <div className="form-hint">
              Select the RDF member firm you represent. Not listed? Contact us.
            </div>
          </div>

          <div className="pwd-section">
            <div className="form-group" style={{ gridArea: 'password' }}>
              <label className="form-label">Password <span className="req">*</span></label>
              <input
                className={`form-input ${errors.password ? 'is-error' : ''}`}
                type="password"
                {...field('password')}
                placeholder="Create a password"
                autoComplete="new-password"
              />
              {errors.password && <div className="form-error">{errors.password}</div>}
            </div>
            <div className="form-group" style={{ gridArea: 'confirm' }}>
              <label className="form-label">Confirm Password <span className="req">*</span></label>
              <input
                className={`form-input ${errors.confirm ? 'is-error' : ''}`}
                type="password"
                {...field('confirm')}
                placeholder="Repeat your password"
                autoComplete="new-password"
              />
              {errors.confirm && <div className="form-error">{errors.confirm}</div>}
            </div>
            <div style={{ gridArea: 'hints' }}>
              <PasswordChecker password={form.password} />
            </div>
          </div>

          <button className="btn btn-primary btn-full" type="submit" disabled={isLoading}>
            {isLoading ? 'Creating Account…' : 'Create Account'}
          </button>
        </form>

        <div className="auth-switch" style={{ marginTop: 16 }}>
          Already have an account? <span onClick={() => navigate('/login')}>Log In</span>
        </div>
      </div>
    </div>
  );
};
